import { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { useQueryClient } from '@tanstack/react-query';
import useSound from 'use-sound';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapObject } from '../models/mapObject';
import { useTonConnect } from '../hooks/useTonConnect';
import { useUserData } from '../hooks/useUserData';
import posthog from 'posthog-js';
import MapTopBar from './map/components/MapTopBar';
import MapModals from './map/components/MapModals';
import { useMapAdminInteraction } from './map/useMapAdminInteraction';
import { useMapClickHandlers } from './map/useMapClickHandlers';
import { useMapObjectsRender } from './map/useMapObjectsRender';
import { MapController } from './map/controller/MapController';
import { MarkerFactory } from './map/controller/markers/MarkerFactory';
import { HighlightManager } from './map/controller/HighlightManager';
import { CameraController } from './map/controller/CameraController';
import { useMapQueries } from './map/useMapData';
import { useLighthouse } from './map/useLighthouse';
import { useCursorPopups } from './map/useCursorPopups';
import { useMapCulling } from './map/useMapCulling';
import { useWorldWrap } from './map/worldWrap';
import { useIncrementalMapObjects } from './map/useIncrementalMapObjects';
import { useViewportBackgroundClick } from './map/useViewportBackgroundClick';
import { useAdminViewportClick } from './map/useAdminViewportClick';
import { useQuestsPrefetch, useQuestMarkers } from './map/useMapQuests';
import { useMarkerVisibility } from './map/useMarkerVisibility';
import { useMayorStoriesGate, useMayorStories } from './map/useMayorStories';
import { clearPendingCityHallVoteDeepLink, hasPendingCityHallVoteDeepLink } from '../hooks/useDeepLinkStartParam';
import {
  useOnboardingFirstMove,
  useOnboardingFlow,
  useTutorialRewardClaim,
  useOnboardingZoomToPlot,
} from './map/useMapOnboarding';
import {
  useSelectedMapObjectSync,
  useReturnToMapObjectRestore,
} from './map/useSelectedMapObjectSync';

// fetchListingForHouse / fetchMarketplaceRate moved to ./map/useLighthouse.ts
// (sole consumer was the lighthouse cluster, which moved there wholesale).

// fetchCursorUserInfo moved to ./map/useCursorPopups.ts (sole consumer).

// fetchCityHallState moved to ./map/useMayorStories.ts (sole consumer was the
// prefetch effect that moved there).

// fetchElectionDialogState moved to ./map/useMapData.ts (shared by the prefetch
// effect below and useMapQueries' election-dialog-state query).
// Map/Welcome-онбординг читаємо з БД (useOnboardingState), щоб «Скинути онбординг»
// справді працював після видалення рядків user_onboarding_kv.
import { useOnboardingState } from '../hooks/useOnboardingState';
import { useDailyRewardsAutoOpen } from '../hooks/useDailyRewardsAutoOpen';
import useSoundStore from '../stores/useSoundStore';
import useInterfaceStore from '../stores/useInterfaceStore';
import useMapModalStore from '../stores/useMapModalStore';
import useMapOnboardingStore from '../stores/useMapOnboardingStore';
import useMapViewStore from '../stores/useMapViewStore';
import { useCreateUserAction } from '../hooks/useCreateUserAction';
import ActiveGameZoneScreenWithNavigation from '../components/ActiveGameZoneScreenWithNavigation';
import { useTranslation } from 'react-i18next';
import { MERCURY_X, MERCURY_Y } from './map/mapCamera';
import { CLOUDS_OVERLAY_PATH, ISLAND_TYPE_TO_IMAGE } from './map/mapPreload';
import {
  REAL_ESTATE_FIXED_SIZES,
  PLOT_MARKER_SIZE,
  STATIC_OBJECT_FIXED_SIZES,
} from './map/mapMarkerSizes';

const MOVE_THRESHOLD_MOUSE = 10;
const MOVE_THRESHOLD_TOUCH = 5;
const CLOUDS_FADE_START_SCALE = 0.85;
const CLOUDS_FADE_END_SCALE = 0.7;
const CLOUDS_MAX_ALPHA = 0.6;

// ONBOARDING_REWARD_* consts + OnboardingRewardAmountRow moved to
// ./map/components/MapModals.tsx (sole consumer, map-architecture Phase 8).

/**
 * MapCanvas lifecycle guide (singleton PixiJS)
 *
 * Purpose:
 * - We keep ONE shared PIXI.Application for the map to avoid GPU leaks/crashes
 *   from frequent mount/unmount cycles.
 * - On unmount we pause the app and clear dynamic markers, but DO NOT destroy
 *   the Application itself.
 *
 * Entry/Exit flow:
 * - Mount: attach existing canvas OR init once, start ticker, enable events.
 * - Unmount: stop ticker, disable events, detach canvas, clear markers/highlight.
 *
 * Adding new map objects:
 * - Use the existing marker factories (`createPlotIcon`, `createObjectMarker`,
 *   `createQuestMarker`) or add a new factory with the same pattern.
 * - Add markers inside the "Render all map objects" effect so they are rebuilt
 *   from API data, not stored permanently.
 * - Always set `eventMode = 'static'`, cursor, and store `mapObjectData` on
 *   the container for click handling.
 *
 * Important rules (do NOT break):
 * - Do not create/destroy a new PIXI.Application on every mount.
 * - Do not keep persistent markers between mounts; rebuild them from data.
 * - If you add new event listeners on singleton objects, ensure they are
 *   re-synced on mount to avoid stale React closures.
 */

// MapSceneSingleton type + mapSceneSingleton const extracted to ./map/mapScene.ts (step 05a).
import { setLastMapObjectsRenderKey, clearMapObjectHighlightSingleton } from './map/mapScene';
import { createMapSceneState } from './map/mapSceneState';
import { MAP_ANALYTICS_KEYS } from './map/analyticsOnce';

/** Tracks all texture paths loaded by MapCanvas for cleanup on unmount. */
const mapLoadedTexturePaths = new Set<string>();

// ── Scene-layer state: MapSceneState (scene-state step 02) + SceneManager ────
// PIXI layers/markers/flags/admin live on MapSceneState. Analytics once-trackers,
// mapEnterTs, didInitialHouseCamera, decorAtlasSheet live there too.
// SceneManager keeps mount/unmount lifecycle + texture cleanup only.

// Marker pointerdown handlers live at module scope (not useRef) because markers are
// kept alive across room/cityhall navigation and their pointerdown closures capture
// these ref objects. A component-level useRef would hand each remount a NEW object,
// leaving persisted markers calling the previous (unmounted) instance's stale handler.
// MapCanvas is a singleton scene (one instance at a time), so a shared module-level
// ref is safe and is refreshed to the current instance's handler on every render.
type MarkerClickHandler = (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;
const handlePlotClickRef: { current: MarkerClickHandler | null } = { current: null };
const handleMapObjectClickRef: { current: MarkerClickHandler | null } = { current: null };

// getMapObjectRenderSignaturePiece moved to ./map/useMapData.ts (shared by the
// mapObjectsSignature memo there and the added/removed/modified diff below).

// ClickStartData moved to ./map/useMapClickHandlers.ts (sole consumer,
// map-architecture Phase 8) — imported back below for the clickStartRef type.

// AdminClickData + adminClickStartRef + the double-click thresholds moved to
// ./map/useAdminViewportClick.ts (sole consumer).

// centerMapOnCityHall → ./map/mapCamera.ts; marker sizes → ./map/mapMarkerSizes.ts

const MapCanvas = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { createUserAction } = useCreateUserAction();
  const { returnToMapObjectId, clearReturnToMapObjectId, purchaseHighlightMapObjectId, setPurchaseHighlightMapObjectId, pendingDeepLinkFocus, clearPendingDeepLinkFocus, setPendingDeepLinkFocus } = useInterfaceStore();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  // Single object grouped by meaning. createMapSceneState() runs once as the
  // useRef initializer (same timing as the former individual useRefs seeded
  // from mapSceneSingleton). Nested groups have stable identity for the scene
  // lifetime so controllers/hooks capture groups, not ephemeral wrappers.
  const sceneRef = useRef(createMapSceneState());

  const scheduleDeleteGuardClear = (mapObjectId: number) => {
    window.setTimeout(() => {
      sceneRef.current.data.deletingMapObjectIds.delete(mapObjectId);
    }, 1000);
  };
  
  // ── Core view/interaction state: src/stores/useMapViewStore.ts (Phase 3) ──
  const isAdminMode = useMapViewStore((s) => s.isAdminMode);
  const setIsAdminMode = useMapViewStore((s) => s.setIsAdminMode);
  /** Актуальний «режим бога» — sceneRef.current.flags.godModeActive. */
  const selectedMapObject = useMapViewStore((s) => s.selectedMapObject);
  const setSelectedMapObject = useMapViewStore((s) => s.setSelectedMapObject);
  const showPlots = useMapViewStore((s) => s.showPlots);
  const setShowPlots = useMapViewStore((s) => s.setShowPlots);
  // Mirror into scene bag — same timing as former showPlotsRef.current = showPlots (render).
  // eslint-disable-next-line react-hooks/refs -- intentional live mirror for PIXI handlers
  sceneRef.current.flags.showPlots = showPlots;
  // ── Modal UI state: src/stores/useMapModalStore.ts (discriminated activeModal + shared payload) ──
  const activeModal = useMapModalStore((s) => s.activeModal);
  const openModal = useMapModalStore((s) => s.openModal);
  const closeModal = useMapModalStore((s) => s.closeModal);
  const isPlotFreeModalOnboarding = useMapModalStore((s) => s.payload.plotFreeOnboarding);
  const setPlotFreeOnboarding = useMapModalStore((s) => s.setPlotFreeOnboarding);
  const setOpenOnboardingPlotFreeAfterReward = useMapModalStore((s) => s.setOpenPlotFreeAfterReward);
  const setPendingMapObjectCreation = useMapModalStore((s) => s.setPendingMapObjectCreation);
  const setElectionCityHallScreen = useMapModalStore((s) => s.setElectionCityHallScreen);
  const isRealEstateModalOpen = activeModal === 'realEstate';
  const isRealEstateInfoModalOpen = activeModal === 'realEstateInfo';
  const isMapObjectModalOpen = activeModal === 'mapObject';
  const isPlotFreeModalOpen = activeModal === 'plotFree';
  // isPlotTakenModalOpen / isNewIslandModalOpen / isDailyQuestsModalOpen /
  // isMapObjectTypeSelectionModalOpen / isSalePreviewModalOpen / isWinstonCatModalOpen
  // recomputed inside MapModals (their only consumer, map-architecture Phase 8).
  const isMapOnboardingModalOpen = activeModal === 'mapOnboarding';
  const isLighthouseModalOpen = activeModal === 'lighthouse';
  const isMapWelcomeModalOpen = activeModal === 'mapWelcome';
  const isTutorialRewardModalOpen = activeModal === 'tutorialReward';
  const { isOpen: isDailyRewardsModalOpen, onClose: onDailyRewardsModalClose } = useDailyRewardsAutoOpen();
  // ── Onboarding-flow state: src/stores/useMapOnboardingStore.ts ──
  const isOnboardingZooming = useMapOnboardingStore((s) => s.isOnboardingZooming);
  const setIsOnboardingZooming = useMapOnboardingStore((s) => s.setIsOnboardingZooming);
  const shouldZoomToPlot = useMapViewStore((s) => s.shouldZoomToPlot);
  const setShouldZoomToPlot = useMapViewStore((s) => s.setShouldZoomToPlot);
  const isOnboardingBuildHousePopupOpen = useMapOnboardingStore((s) => s.isOnboardingBuildHousePopupOpen);
  const markersReady = useMapViewStore((s) => s.markersReady);
  const setMarkersReady = useMapViewStore((s) => s.setMarkersReady);
  const setFirstVisitPhase = useMapOnboardingStore((s) => s.setFirstVisitPhase);
  const [isTestingSentry, setIsTestingSentry] = useState(false);
  const isPixiAppReady = useMapViewStore((s) => s.isPixiAppReady);
  const setIsPixiAppReady = useMapViewStore((s) => s.setIsPixiAppReady);
  // The ten `lighthouse*` state pairs moved to ./map/useLighthouse.ts together
  // with their effect + handlers; the hook is called after useTonConnect below
  // (it needs the wallet handles) and its result is destructured there.
  // tutorialRewardClaiming / rewardPhase / totalPearlsAwarded /
  // totalExpAwarded moved to ./map/useMapOnboarding.ts (useTutorialRewardClaim).
  // showMayorStories + electionDialogCheckedRef moved to ./map/useMayorStories.ts.

  // clickStart / adminObjectModes live on sceneRef.current.admin
  const [showAdminRadialMenu, setShowAdminRadialMenu] = useState(false);
  const [adminRadialMenuPosition, setAdminRadialMenuPosition] = useState({ x: 0, y: 0 });
  const [adminRadialMenuMapObject, setAdminRadialMenuMapObject] = useState<{ mapObject: MapObject; marker: PIXI.Container } | null>(null);
  const [adminRadialObjectMode, setAdminRadialObjectMode] = useState<'dnd' | 'resize'>('dnd');
  const [highlightedAdminAction, setHighlightedAdminAction] = useState<string | null>(null);
  const [adminResizeDims, setAdminResizeDims] = useState<{ w: number; h: number } | null>(null);
  // admin radial/selection state lives on sceneRef.current.admin

  const { soundsEnabled, oceanDucked } = useSoundStore();
  const [playWavesSound, { stop: stopWavesSound }] = useSound(
    '/game-assets/sounds/ocean-waves.mp3',
    {
      volume: 0.08,
      loop: true,
      soundEnabled: soundsEnabled,
    }
  );

  const { data: userData } = useUserData();
  const { connected: lhWalletConnected, sendTon: lhSendTon, sendUsdt: lhSendUsdt, connectWallet: lhConnectWallet } = useTonConnect();
  // ── Lighthouse cluster: ./map/useLighthouse.ts ──
  // Called here (not at the old state-declaration site) because it needs the
  // useTonConnect handles declared just above; every consumer of its state
  // (useMapClickHandlers, MapModals) comes later in the file.
  const lighthouse = useLighthouse({
    isLighthouseModalOpen,
    queryClient,
    t,
    lhWalletConnected,
    lhSendTon,
    lhSendUsdt,
    lhConnectWallet,
  });
  const { setLighthouseObject } = lighthouse;
  const onboardingState = useOnboardingState();
  const {
    hasOnboardingCompleted,
    hasTapOnboardingDone: hasTapDone,
    hasMapOnboardingDone: hasMapTutorialDoneForMonth,
    hasMapWelcomeOnboardingDone: hasMapWelcomeDoneForMonth,
  } = onboardingState;
  const onboardingQueryData = onboardingState.data;
  const onboardingCompleted = hasOnboardingCompleted();
  // ── Election-dialog onboarding gate: ./map/useMayorStories.ts ──
  // Must stay ABOVE useMapQueries — it feeds the election-state query's enabled flag.
  const { isMayorStoriesBlockedByOnboarding } = useMayorStoriesGate({
    onboardingCompleted,
  });
  // eslint-disable-next-line react-hooks/refs -- intentional live mirror for PIXI handlers
  sceneRef.current.flags.godModeActive = !!(isAdminMode && userData?.is_admin);

  // ── Onboarding step 0 trigger (first map movement): ./map/useMapOnboarding.ts ──
  useOnboardingFirstMove({
    isPixiAppReady,
    onboardingQueryData,
    sceneRef,
    hasTapDone,
    queryClient,
  });

  // ── Live-cursor / firefly popup cluster: ./map/useCursorPopups.ts ──
  const {
    cursorPopup,
    setCursorPopup,
    cursorPopupLoading,
    setCursorPopupLoading,
    fireflyPopup,
    setFireflyPopup,
    fireflyPopupLoading,
    setFireflyPopupLoading,
    fireflyRecordedAt,
  } = useCursorPopups({ sceneRef, userId: userData?.user_id });

  // Map data-fetch + derived cluster — extracted to ./map/useMapData.ts (useMapQueries).
  const {
    mapObjects,
    isFetching,
    isPending,
    listedHouseIds,
    listingsByHouseId,
    plots,
    houses,
    cityhalls,
    shops,
    hellOfFames,
    decorObjects,
    islands,
    mapObjectsSignature,
    getLatestMapObject,
    dailyQuestsData,
    dailyQuestsUpdatedAt,
    hourlyQuestsData,
    marketingQuestsData,
    listedHouseIdsKey,
    mapObjectsRenderKey,
    electionDialogStateData,
  } = useMapQueries({ queryClient, userData, isMayorStoriesBlockedByOnboarding });

  // Keep data mirrors fresh — same timing as former mapObjectsRef.current = mapObjects (render).
  /* eslint-disable react-hooks/refs -- intentional live mirrors for PIXI handlers / incremental effects */
  sceneRef.current.data.mapObjects = mapObjects;
  sceneRef.current.data.islands = islands;
  sceneRef.current.data.listingsByHouseId = listingsByHouseId;
  sceneRef.current.data.listedHouseIds = listedHouseIds;
  /* eslint-enable react-hooks/refs */

  // lastRenderKey seeded from singleton in createMapSceneState(); mirror into
  // singleton so it survives unmount→remount (room ↔ map navigation).
  const setLastRenderKey = (key: string | null) => {
    sceneRef.current.viewport.lastRenderKey = key;
    setLastMapObjectsRenderKey(key);
  };

  useEffect(() => {
    if (soundsEnabled && !oceanDucked) {
      playWavesSound();
    } else {
      stopWavesSound();
    }

    return () => {
      stopWavesSound();
    };
  }, [soundsEnabled, oceanDucked, playWavesSound, stopWavesSound]);

  // ── Election dialog: ./map/useMayorStories.ts ──
  // Carries the city-hall-state + election-state prefetch effect and the
  // show/hide effect, in their original order.
  const { showMayorStories, dismissMayorStories, handleElectionVote, handleElectionRegister } = useMayorStories({
    userData,
    isMayorStoriesBlockedByOnboarding,
    electionDialogStateData,
    cityhalls,
    queryClient,
    setSelectedMapObject,
    setElectionCityHallScreen,
    openModal,
  });

  // ── Selected-object sync: ./map/useSelectedMapObjectSync.ts ──
  useSelectedMapObjectSync({
    selectedMapObject,
    mapObjects,
    isPending,
    isFetching,
    isRealEstateModalOpen,
    isPlotFreeModalOpen,
    isRealEstateInfoModalOpen,
    isMapObjectModalOpen,
    sceneRef,
    setSelectedMapObject,
  });


  // ── Nav blocking + welcome/tutorial/zoom-hint sequence: ./map/useMapOnboarding.ts ──
  useOnboardingFlow({
    isMapOnboardingModalOpen,
    isTutorialRewardModalOpen,
    isMapWelcomeModalOpen,
    isOnboardingZooming,
    isOnboardingBuildHousePopupOpen,
    isPlotFreeModalOpen,
    isPlotFreeModalOnboarding,
    onboardingQueryData,
    isPixiAppReady,
    hasOnboardingCompleted,
    hasTapDone,
    hasMapWelcomeDoneForMonth,
    hasMapTutorialDoneForMonth,
    queryClient,
    openModal,
    navigate,
    setFirstVisitPhase,
  });

  // ── House-modal restore after leaving the interior: ./map/useSelectedMapObjectSync.ts ──
  useReturnToMapObjectRestore({
    returnToMapObjectId,
    mapObjects,
    setSelectedMapObject,
    openModal,
    clearReturnToMapObjectId,
  });

  // ── Tutorial-reward auto-claim: ./map/useMapOnboarding.ts ──
  const tutorialReward = useTutorialRewardClaim({
    isTutorialRewardModalOpen,
    queryClient,
    setOpenOnboardingPlotFreeAfterReward,
    closeModal,
  });



  // ── Quest prefetch + "unfinished quests" badge: ./map/useMapQuests.ts ──
  const { hasUnfinishedQuests } = useQuestsPrefetch({
    dailyQuestsData,
    hourlyQuestsData,
    marketingQuestsData,
    queryClient,
  });

  useEffect(() => {
    // Don't show gold highlight in admin/god mode
    if (sceneRef.current.flags.godModeActive) return;

    if (selectedMapObject && sceneRef.current.layers.mapObjects && sceneRef.current.pixi.app && isPixiAppReady) {
      const tryRestoreHighlight = () => {
        let marker: PIXI.Container | null = null;

        if (selectedMapObject.type === 'plot') {
          marker = sceneRef.current.markers.plots.get(selectedMapObject.id) || null;
        } else if (selectedMapObject.type === 'house') {
          marker = sceneRef.current.markers.realEstate.get(selectedMapObject.id) || null;
        } else if (selectedMapObject.type === 'cityhall') {
          marker = sceneRef.current.markers.mapObjects.get(selectedMapObject.id) || null;
        }

        if (marker && !sceneRef.current.sprites.mapObjectHighlight) {
          highlightManagerRef.current!.apply(selectedMapObject, marker);
          return true;
        }
        return false;
      };

      if (tryRestoreHighlight()) {
        return;
      }

      const timeoutId = setTimeout(() => {
        tryRestoreHighlight();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedMapObject, isPixiAppReady, isAdminMode, userData?.is_admin]);

  // Scene-layer Facade (map-architecture Phase 4). Owns the PIXI scene lifecycle
  // that used to live in the `useMapScene` hook + the 7 module-level `let`s. The
  // controller is created once (deps captured on first render, exactly like the
  // old `[]`-dep useMapScene effect closed over its first-render params) and its
  // mount/unmount drive the scene from a single `[]` effect below — preserving
  // the original [scene mounts → setIsPixiAppReady(true) → render effect →
  // markersReady] ordering byte-for-byte.
  /* eslint-disable react-hooks/refs, react-hooks/immutability -- once-per-mount useRef-guard facade construction (unchanged timing) */
  const controllerRef = useRef<MapController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new MapController({
      scene: sceneRef.current,
      setIsPixiAppReady, setLastRenderKey, mapLoadedTexturePaths,
      CLOUDS_OVERLAY_PATH, CLOUDS_FADE_START_SCALE,
      CLOUDS_FADE_END_SCALE, CLOUDS_MAX_ALPHA, MERCURY_X, MERCURY_Y,
    });
  }
  const markerFactoryRef = useRef<MarkerFactory | null>(null);
  if (!markerFactoryRef.current) {
    markerFactoryRef.current = new MarkerFactory({
      mapLoadedTexturePaths,
      getDecorAtlasSheet: () => sceneRef.current.assets.decorAtlasSheet,
      REAL_ESTATE_FIXED_SIZES,
      STATIC_OBJECT_FIXED_SIZES,
    });
  }
  // Highlight + camera Facades (map-architecture Phase 6, P5). Created once,
  // same useRef-guard pattern as controllerRef/markerFactoryRef above.
  const cameraControllerRef = useRef<CameraController | null>(null);
  const highlightManagerRef = useRef<HighlightManager | null>(null);
  if (!highlightManagerRef.current) {
    highlightManagerRef.current = new HighlightManager({
      scene: sceneRef.current,
      setSelectedMapObject, openModal,
      REAL_ESTATE_FIXED_SIZES, STATIC_OBJECT_FIXED_SIZES, PLOT_MARKER_SIZE,
      // Late-bound: CameraController is created next; animate only runs after mount.
      animateCamera: (params, opts) => cameraControllerRef.current!.animate(params, opts),
    });
  }
  if (!cameraControllerRef.current) {
    cameraControllerRef.current = new CameraController({
      scene: sceneRef.current,
      applyHighlight: (mapObject, marker) => highlightManagerRef.current!.apply(mapObject, marker),
      clearPendingDeepLinkFocus,
      openModal,
    });
  }
  /* eslint-enable react-hooks/refs, react-hooks/immutability */
  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const c = controllerRef.current!;
    c.mount(canvasEl);
    return () => c.unmount();
  }, []);

  useMapObjectsRender({
    mapObjectsRenderKey,
    isPixiAppReady,
    sceneRef,
    highlight: {
      clearSingleton: clearMapObjectHighlightSingleton,
    },
    assets: {
      loadedTexturePaths: mapLoadedTexturePaths,
      islandTypeToImage: ISLAND_TYPE_TO_IMAGE,
    },
    renderStatus: {
      setLastRenderKey,
      setMarkersReady,
    },
    clickHandlers: {
      plots: handlePlotClickRef,
      mapObjects: handleMapObjectClickRef,
    },
    selectedMapObject,
    objectGroups: {
      plots,
      houses,
      cityhalls,
      shops,
      hellOfFames,
      decorObjects,
    },
    markerBuilders: {
      realEstate: (obj) => markerFactoryRef.current!.createRealEstateMarker(obj),
      mapObject: (obj) => markerFactoryRef.current!.createMapObjectMarker(obj),
    },
  });

  // ── Marker visibility + sale tags: ./map/useMarkerVisibility.ts ──
  useMarkerVisibility({
    showPlots,
    markersReady,
    isPixiAppReady,
    listedHouseIdsKey,
    sceneRef,
  });

  // ── Quest markers: ./map/useMapQuests.ts (useQuestMarkers) ──
  // `hideQuestMarker` is a thunk — it is a `const` arrow declared below.
  useQuestMarkers({
    markersReady,
    isPixiAppReady,
    dailyQuestsUpdatedAt,
    dailyQuestsData,
    sceneRef,
    markerFactoryRef,
    highlightManagerRef,
    getLatestMapObject,
    hideQuestMarker: (mapObject) => hideQuestMarker(mapObject),
    setSelectedMapObject,
    openModal,
    createUserAction,
  });

  // ── Onboarding zoom-to-plot: ./map/useMapOnboarding.ts ──
  useOnboardingZoomToPlot({
    location,
    shouldZoomToPlot,
    markersReady,
    isPixiAppReady,
    plots,
    cityhalls,
    sceneRef,
    highlightManagerRef,
    setIsOnboardingZooming,
    setShouldZoomToPlot,
    setSelectedMapObject,
    setPlotFreeOnboarding,
    setOpenOnboardingPlotFreeAfterReward,
    openModal,
    hasOnboardingCompleted,
  });

  // ── Initial camera: fly to a random owned house on first map load (CameraController) ──
  useEffect(() => {
    cameraControllerRef.current!.runInitialHouseCamera(isPixiAppReady, markersReady, userData?.user_id,
      shouldZoomToPlot, purchaseHighlightMapObjectId, pendingDeepLinkFocus !== null);
  }, [isPixiAppReady, markersReady, userData?.user_id, shouldZoomToPlot, purchaseHighlightMapObjectId, pendingDeepLinkFocus, mapObjectsSignature]);
  useEffect(() => () => cameraControllerRef.current?.cancelInitialHouseCamera(), []);

  // ── Multi-stage map load tracking ─────────────────────────────────
  // Stage 1: PixiJS app created, canvas in DOM, ocean background drawn.
  // Stages 2-4 are tracked inside the async render IIFE (see below).
  useEffect(() => {
    if (!isPixiAppReady || sceneRef.current.analytics.once.fired(MAP_ANALYTICS_KEYS.pixiInit)) return;
    sceneRef.current.analytics.once.markFired(MAP_ANALYTICS_KEYS.pixiInit);
    // Load-timing: mark map entry once per map session (start of the load window).
    if (sceneRef.current.analytics.mapEnterTs === 0) sceneRef.current.analytics.mapEnterTs = performance.now();
    try { posthog.capture('map_pixi_initialized'); } catch { /* never block game */ }
  }, [isPixiAppReady]);

  // ── Viewport culling: ./map/useMapCulling.ts ──
  useMapCulling({
    sceneRef,
    isPixiAppReady,
    mapObjectsRenderKey,
  });

  // ── The map wraps around at its edges: ./map/worldWrap.ts ──
  useWorldWrap({ sceneRef, isPixiAppReady, markersReady, mapObjectsRenderKey });

  // ── Incremental map-object diff: ./map/useIncrementalMapObjects.ts ──
  // Called from the original position so the useLayoutEffect still runs before
  // the main PIXI render effect.
  useIncrementalMapObjects({
    mapObjects,
    mapObjectsRenderKey,
    isPixiAppReady,
    markersReady,
    sceneRef,
    handlePlotClickRef,
    handleMapObjectClickRef,
    markerFactoryRef,
    setLastRenderKey,
    ISLAND_TYPE_TO_IMAGE,
    mapLoadedTexturePaths,
  });

  // ── Background (non-marker) viewport click: ./map/useViewportBackgroundClick.ts ──
  // `getMoveThreshold` / `closeAdminRadialMenu` are wrapped in thunks: both are
  // `const` arrows declared further down this file, so they cannot be read at
  // hook-call time — only later, from inside the PIXI handlers.
  useViewportBackgroundClick({
    sceneRef,
    setSelectedMapObject,
    closeAdminRadialMenu: () => closeAdminRadialMenu(),
    getMoveThreshold: (event) => getMoveThreshold(event),
    selectedMapObject,
  });

  const getMoveThreshold = (event: PIXI.FederatedPointerEvent): number => {
    const pointerType = event.pointerType || 'mouse';
    const isTouch = pointerType === 'touch' || pointerType === 'pen';
    return isTouch ? MOVE_THRESHOLD_TOUCH : MOVE_THRESHOLD_MOUSE;
  };

  // Top100 / HousesScreen: виставляють purchaseHighlightMapObjectId без прямого виклику з дочірнього UI.
  // (body moved to HighlightManager.runPurchaseAnim — map-architecture Phase 6, P5)
  useEffect(() => {
    const purchaseId = purchaseHighlightMapObjectId;
    if (!purchaseId) return;

    const cleanup = highlightManagerRef.current!.runPurchaseAnim(purchaseId, markersReady, isPixiAppReady, userData);
    if (cleanup) {
      setPurchaseHighlightMapObjectId(null);
      return cleanup;
    }
    /* Маркер ще не готовий — id лишається в store, ефект повториться з deps. */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- тригер: id + готовність мапи (highlightManagerRef.current!.runPurchaseAnim — імперативний виклик, не React callback)
  }, [purchaseHighlightMapObjectId, markersReady, isPixiAppReady, mapObjectsSignature, setPurchaseHighlightMapObjectId]);

  // ── Deep-link camera focus (Telegram startapp: plot_<id> | cityhall) ─────────
  // Fires once after the map is ready, and only for players who finished
  // onboarding: onboarding is the single entry point, so a deep link opened by
  // a newbie is dropped instead of popping City Hall / a house over the
  // tutorial. Clears the target after use so view-switches don't re-trigger.
  // (branch bodies moved to CameraController.zoomToPlot/centerOnCityHall — Phase 6)
  useEffect(() => {
    // Restore vote intent after StrictMode remount / premature clearPendingDeepLinkFocus.
    let target = pendingDeepLinkFocus;
    if (!target && hasPendingCityHallVoteDeepLink()) {
      target = { type: 'cityhall', openVote: true };
      setPendingDeepLinkFocus(target);
    }
    if (!target) return;
    if (!hasOnboardingCompleted()) {
      clearPendingDeepLinkFocus();
      clearPendingCityHallVoteDeepLink();
      return;
    }
    if (!markersReady || !isPixiAppReady || !sceneRef.current.pixi.viewport) return;
    // Defer while the onboarding zoom sequence is in progress (it re-fires once done)
    if (shouldZoomToPlot) return;

    if (target.type === 'plot') {
      cameraControllerRef.current!.zoomToPlot(target, hasOnboardingCompleted);
      return;
    }

    if (target.type === 'cityhall') {
      const ch =
        cityhalls[0] ??
        sceneRef.current.data.mapObjects.find((o) => o.type === 'cityhall');

      if (target.openVote) {
        // Wait until cityhall exists — do NOT clear pending / session flag yet.
        if (!ch) return;
        dismissMayorStories();
        setSelectedMapObject(ch);
        setElectionCityHallScreen('vote_list');
        openModal('mapObject');
        // Camera without clearing — we clear below only after a successful open.
        if (typeof ch.x === 'number' && typeof ch.y === 'number') {
          cameraControllerRef.current!.animate({
            position: { x: ch.x, y: ch.y },
            scale: 1.0,
            time: 900,
          });
        }
        clearPendingDeepLinkFocus();
        clearPendingCityHallVoteDeepLink();
        return;
      }

      cameraControllerRef.current!.centerOnCityHall();
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deep-link focus triggers; camera helpers via refs
  }, [pendingDeepLinkFocus, markersReady, isPixiAppReady, mapObjectsSignature, shouldZoomToPlot, cityhalls]);

  // setupClickHandlers moved to ./map/useMapClickHandlers.ts (sole consumer
  // was handleMapObjectClick/handlePlotClick, also moved there).

  const hideQuestMarker = (mapObject: MapObject): void => {
    const questMarker = sceneRef.current.markers.quests.get(mapObject.id);
    if (questMarker && sceneRef.current.layers.mapObjects) {
      sceneRef.current.layers.mapObjects.removeChild(questMarker);
      questMarker.destroy();
      sceneRef.current.markers.quests.delete(mapObject.id);
    }
  };

  const clearAdminSelection = () => {
    const prev = sceneRef.current.admin.selectedMarker;
    if (prev?.marker && !prev.marker.destroyed) {
      const graphics = prev.marker.children.filter(c => c instanceof PIXI.Graphics);
      for (const g of graphics) {
        prev.marker.removeChild(g);
        g.destroy();
      }
    }
    sceneRef.current.admin.selectedMarker = null;
  };

  const deactivateAdminMarkerVisual = () => {
    const prev = sceneRef.current.admin.radialMarkerState;
    if (prev?.marker?.scale) {
      prev.marker.scale.set(prev.scaleX, prev.scaleY);
    }
    sceneRef.current.admin.radialMarkerState = null;
  };

  const getAdminRadialPositions = (count: number) => {
    const topOuterY = -140;
    const bottomOuterY = 140;
    const midY = -50;
    const midLowY = 50;
    if (count === 1) return [{ xOffset: 0, yOffset: topOuterY }];
    if (count === 2) return [{ xOffset: 0, yOffset: topOuterY }, { xOffset: 0, yOffset: bottomOuterY }];
    if (count === 3) return [
      { xOffset: 0, yOffset: topOuterY },
      { xOffset: 0, yOffset: midY },
      { xOffset: 0, yOffset: bottomOuterY },
    ];
    if (count === 4) return [
      { xOffset: 0, yOffset: topOuterY },
      { xOffset: 0, yOffset: midY },
      { xOffset: 0, yOffset: midLowY },
      { xOffset: 0, yOffset: bottomOuterY },
    ];
    // 5+ options (e.g. resize mode adds "Default size"): distribute evenly so
    // positions[index] is never undefined — otherwise openAdminRadialMenu throws
    // and the radial menu silently fails to open (object stuck in resize mode).
    const spacing = 70;
    const startY = -((count - 1) * spacing) / 2;
    return Array.from({ length: count }, (_, i) => ({
      xOffset: 0,
      yOffset: Math.round(startY + i * spacing),
    }));
  };

  const closeAdminRadialMenu = () => {
    setShowAdminRadialMenu(false);
    setAdminRadialMenuMapObject(null);
    setHighlightedAdminAction(null);
    sceneRef.current.admin.radialCenters = [];
    deactivateAdminMarkerVisual();
  };

  const {
    attachIslandGodPointerHandlers,
    executeAdminRadialAction,
    setupAdminDragAndDrop,
  } = useMapAdminInteraction({
    sceneRef,
    setPendingMapObjectCreation,
    openMapObjectTypeSelectionModal: () => openModal('typeSelection'),
    setAdminRadialMenuPosition,
    setAdminRadialMenuMapObject,
    setShowAdminRadialMenu,
    setHighlightedAdminAction,
    setAdminRadialObjectMode,
    setAdminResizeDims,
    clearAdminSelection,
    closeAdminRadialMenu,
    getAdminRadialPositions,
    scheduleDeleteGuardClear,
    MOVE_THRESHOLD_TOUCH,
    MOVE_THRESHOLD_MOUSE,
    REAL_ESTATE_FIXED_SIZES,
    PLOT_MARKER_SIZE,
    STATIC_OBJECT_FIXED_SIZES,
  });

  useEffect(() => {
    if (!markersReady || !isPixiAppReady) return;
    const god = !!(isAdminMode && userData?.is_admin);
    sceneRef.current.markers.islands.forEach((container, islandId) => {
      const island = sceneRef.current.data.mapObjects.find(
        (o) => o.id === islandId && o.type.startsWith('island_')
      );
      if (!island || !container || container.destroyed) return;

      container.removeAllListeners();

      if (god) {
        container.eventMode = 'static';
        container.cursor = 'default';
        attachIslandGodPointerHandlers(container, island);
      } else {
        container.eventMode = 'none';
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- attachIslandGodPointerHandlers змінюється щорендеру; тригер — god mode / маркери
  }, [isAdminMode, userData?.is_admin, markersReady, isPixiAppReady, mapObjectsSignature]);

  // handleMapObjectClick / handlePlotClick + their shared setupClickHandlers
  // helper moved to ./map/useMapClickHandlers.ts (map-architecture Phase 8).
  // Bodies are byte-identical; every closure dependency is now a param.
  const { handleMapObjectClick, handlePlotClick } = useMapClickHandlers({
    sceneRef,
    highlightManagerRef,
    setupAdminDragAndDrop,
    getLatestMapObject,
    hideQuestMarker,
    getMoveThreshold,
    setSelectedMapObject,
    setLighthouseObject,
    createUserAction,
    openModal,
    listingsByHouseId,
    userData,
    STATIC_OBJECT_FIXED_SIZES,
  });

  // Module-level handler refs (survive room remount); refreshed every render.
  // eslint-disable-next-line react-hooks/immutability -- intentional handler rebinding
  handlePlotClickRef.current = handlePlotClick;
  // eslint-disable-next-line react-hooks/immutability -- intentional handler rebinding
  handleMapObjectClickRef.current = handleMapObjectClick;

  // ── Admin viewport click (god mode): ./map/useAdminViewportClick.ts ──
  useAdminViewportClick({
    sceneRef,
    clearAdminSelection,
    setPendingMapObjectCreation,
    openModal,
    isAdminMode,
    userData,
  });

  // handleCloseModal / handleBuyHouse / handleMapObjectTypeSelect /
  // handleNavigationConfirm moved into modal hosts (store + self-fetch).
  // dismissMayorStories / handleElectionVote: ./map/useMayorStories.ts

  return (
    <ActiveGameZoneScreenWithNavigation>
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
          position: 'relative'
        }}
      >
        <MapTopBar
          userData={userData}
          showPlots={showPlots}
          setShowPlots={setShowPlots}
          isAdminMode={isAdminMode}
          setIsAdminMode={setIsAdminMode}
          sceneRef={sceneRef}
          clearMapObjectHighlight={clearMapObjectHighlightSingleton}
          closeAdminRadialMenu={closeAdminRadialMenu}
          clearAdminSelection={clearAdminSelection}
          isTestingSentry={isTestingSentry}
          setIsTestingSentry={setIsTestingSentry}
          hasUnfinishedQuests={hasUnfinishedQuests}
          openDailyQuestsModal={() => openModal('dailyQuests')}
        />

        {/* Loading overlay — shown until Pixi is ready */}
        {!isPixiAppReady && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#77C0AF',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: '3px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
          </div>
        )}

        <div
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            margin: 0,
            padding: 0,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        />
        {/* Блокуємо взаємодію під час принудового наближення в онбордингу */}
        {isOnboardingZooming && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 500,
              cursor: 'default',
            }}
          />
        )}
        <MapModals
          highlightManagerRef={highlightManagerRef}
          plots={plots}
          lighthouse={lighthouse}
          lhWalletConnected={lhWalletConnected}
          dailyRewards={{ isOpen: isDailyRewardsModalOpen, onClose: onDailyRewardsModalClose }}
          tutorialReward={tutorialReward}
          election={{ showMayorStories, handleElectionVote, handleElectionRegister, dismissMayorStories }}
          admin={{
            adminResizeDims,
            showAdminRadialMenu,
            adminRadialMenuMapObject,
            adminRadialObjectMode,
            getAdminRadialPositions,
            highlightedAdminAction,
            adminRadialMenuPosition,
            closeAdminRadialMenu,
            executeAdminRadialAction,
          }}
          firefly={{
            fireflyPopup,
            fireflyPopupLoading,
            fireflyRecordedAt,
            setFireflyPopup,
            setFireflyPopupLoading,
          }}
          cursor={{
            cursorPopup,
            cursorPopupLoading,
            setCursorPopup,
            setCursorPopupLoading,
          }}
        />
      </div>
    </ActiveGameZoneScreenWithNavigation>
  );
};

export default MapCanvas;
